import fetch from "cross-fetch";
import * as cheerio from 'cheerio';
import { isBrowser } from "browser-or-node";
import { Agent } from "https";

import { Helper } from "../helpers/helper";
// this import is vital for keep alive agents to be applied to the fetch implementation
import { ImisAccessContext, ImisAuthentication, ImisCredentials, ImisRequestVerificationToken, ImisResponse, ImisResponseItems } from "../models/imis.models";
import { PayloadUtility } from "./payload.utility";
import { UrlUtility } from './url.utility';
import { ContextUtility } from './context.utility';

export class RestUtility {

    private static environments: { [key: string]: ImisCredentials };

    public static baseUrl: string;
    public static authentication: ImisAuthentication;
    public static httpsAgent: Agent;

    //#region ENVIRONMENTS

    public static setEnvironments(environments: { [key: string]: ImisCredentials }) {
        this.environments = environments;
    }

    public static setEnvironmentByName(environmentName: string) {
        let { url, username, password, refresh_token } = this.environments[environmentName];
        this.setEnvironment(url, username, password, refresh_token);
    }

    public static setEnvironment(baseUrl: string, username?: string, password?: string, refresh_token?: string) {
        this.baseUrl = baseUrl;
        if (!Helper.isStringNullOrEmpty(username) && !Helper.isStringNullOrEmpty(password)) {
            this.authentication = new ImisAuthentication(username, password, refresh_token);
        } else {
            this.authentication = null;
        }
    }

    public static initializeNodeKeepAlive() {
        // Lazy-load https only in Node (keeps browser bundles clean)
        // azure functions limits max sockets to around 120 before get SNAT port exhaustion
        this.httpsAgent = new Agent({ keepAlive: true, timeout: 60000, maxSockets: 100, maxFreeSockets: 20 });
    }

    //#endregion

    //#region REST METHODS

    // TODO: REVISE THIS FOR PROPER TYPING on RAW RESPONSE CASE
    public static async restApiGetData<T = ImisResponseItems>(url: string, params?: any, rawResponse?: boolean): Promise<T> {
        let data: T = null;

        let requestUrl = UrlUtility.getUrlStringWithParams(url, params);
        try {
            let response = await this.executeFetchRequest(requestUrl);

            data = await this.getJsonResponseData(response, rawResponse);
        } catch (error) {
            console.error(`Error encountered when performing the following request ${requestUrl}: ${error}`);
        }

        return data;
    }

    // TODO: REVISE THIS FOR PROPER TYPING on RAW RESPONSE CASE
    public static async restApiGetDataAll<T = ImisResponse>(url: string, params?: any, rawResponse?: boolean, maximumRecords?: number): Promise<ImisResponseItems<T>> {
        let data: ImisResponseItems<T> = null;

        try {
            let requestParams = Object.assign({}, params);
            requestParams.offset = requestParams.offset || 0;
            requestParams.limit = requestParams.limit || 500;

            let responseItems: T[] = [];
            do {
                // set parameters if available
                if (data != null) {
                    requestParams.offset = data.NextOffset;
                }
                if (maximumRecords != null) {
                    let remainingRecords = maximumRecords - responseItems.length;
                    requestParams.limit = Math.min(requestParams.limit, remainingRecords);
                }

                // check whether limit is 0 which means we have gotten the maximum number of records required
                if (requestParams.limit == 0) {
                    break;
                }

                data = await this.restApiGetData(url, requestParams, rawResponse);
                if (data != null) {
                    responseItems = responseItems.concat(rawResponse ? (<any>data.Items).$values : data.Items);
                } else if (responseItems.length == 0) {
                    throw "Could not retrieve data";
                } else {
                    // return what you already have
                    break;
                }
            } while (data.HasNext);

            if (rawResponse) {
                (<any>data.Items).$values = responseItems;
            } else {
                data.Items = responseItems;
            }
        } catch (error) {
            console.error(error);
        }

        return data;
    }

    public static async restApiSendData<T = ImisResponseItems>(url: string, payloadData: any, method: "POST" | "PUT" = "POST", rawResponse?: boolean): Promise<T> {
        let parsedResponseData: T = null;
        let retryCount = 0;

        while (parsedResponseData == null && retryCount < 3) {
            try {
                let response = await this.executeFetchRequest(url, method, payloadData);
                parsedResponseData = await this.getJsonResponseData(response, rawResponse);
            } catch (error) {
                console.error(`Error encountered when performing the following request (attempt #${retryCount + 1}) ${url}: ${error}`);
            }

            retryCount++;
        }

        return parsedResponseData;
    }

    public static async restApiDeleteData(url: string): Promise<boolean> {
        let dataDeleted: boolean = false;

        try {
            await this.executeFetchRequest(url, "DELETE", null);
            dataDeleted = true;
        } catch (error) {
            console.error(error);
        }

        return dataDeleted;
    }

    public static async getAccessContext(): Promise<ImisAccessContext> {
        let url = this.getBaseUrl() + "/token";

        let payload: any;
        let isRefreshToken: boolean = this.authentication.grant_type == "refresh_token";
        if (isRefreshToken) {
            let { grant_type, username: client_id, password: client_secret, refresh_token } = this.authentication;
            payload = { grant_type, client_id, client_secret, refresh_token };
        } else {
            payload = { ...this.authentication };
        }

        let response = await fetch(url, {
            method: "POST",
            body: new URLSearchParams(payload)
        });
       let accessContext: ImisAccessContext = await response.json();

        // update the refresh token for upcoming access requests
        if (isRefreshToken) {
            this.authentication.refresh_token = accessContext.refresh_token;
        }

        return accessContext;
    }

    //#endregion

    //#region TOKENS ACCESS METHODS

    public static getRequestVerificationToken(): string {
        let result = "";

        // Passthrough authentication - get the __RequestVerificationToken that is injected into the page by the iMIS site.
        let element: HTMLInputElement = <HTMLInputElement>document.getElementById("__RequestVerificationToken");
        if (element != null) {
            result = element.value;
        }

        return result;
    }

    public static async getAccessToken(): Promise<string> {
        let accessContext = await this.getAccessContext();
        return accessContext.access_token;
    }

    public static async getAnonymousRequestVerificationToken(): Promise<ImisRequestVerificationToken> {
        let headerToken = "";
        let cookieToken = "";

        try {
            let response = await fetch(this.getBaseUrl());
            let responseText = await response.text();

            // get the request verification token in the html
            let cHtml = cheerio.load(responseText);
            headerToken = <string>cHtml("[id=__RequestVerificationToken]").val();

            // get the request verification token in the cookie header
            let setCookieHeader = response.headers.get("set-cookie");
            let tokenMatchResults = setCookieHeader.match(/__RequestVerificationToken=(.+?);/);
            if (tokenMatchResults.length > 1) {
                cookieToken = tokenMatchResults[tokenMatchResults.length - 1];
            }
        } catch (error) {
            console.error(error);
        }

        return {
            cookie: cookieToken,
            header: headerToken
        };
    }

    //#endregion

    private static async executeFetchRequest(url: string, method: "GET" | "POST" | "PUT" | "DELETE" = "GET", body?: any): Promise<Response> {
        let requestUrl = this.getBaseUrl() + url;
        let requestConfig = await this.getRequestConfig(method, body);
        let response = <Response>await fetch(requestUrl, <any>requestConfig);
        this.ensureResponseOk(response);

        return response;
    }

    private static ensureResponseOk(response: Response) {
        if (!response.ok) throw response.status;
    }

    private static async getJsonResponseData<T>(response: Response, rawResponse?: boolean): Promise<T> {
        let responseData = await response.json();
        if (!rawResponse) {
            responseData = PayloadUtility.parseImisPayload<T>(responseData);
        }

        return responseData;
    }

    private static async getRequestConfig(method: string, body?: any): Promise<RequestInit> {
        let headers = {
            "Content-Type": "application/json"
        };

        if (isBrowser) {
            let requestVerificationToken: string = this.getRequestVerificationToken();
            if (!Helper.isStringNullOrEmpty(requestVerificationToken)) {
                headers["RequestVerificationToken"] = requestVerificationToken;
            }
        } else {
            // most likely node, check whether accessing as anonymous user
            if (this.authentication != null) {
                let accessToken: string = await this.getAccessToken();
                headers["Authorization"] = `Bearer ${accessToken}`;
            } else {
                let requestVerificationToken: ImisRequestVerificationToken = await this.getAnonymousRequestVerificationToken();
                headers["RequestVerificationToken"] = requestVerificationToken.header;
                headers["Cookie"] = `__RequestVerificationToken=${requestVerificationToken.cookie}`;
            }
        }

        let requestConfig: RequestInit = {
            method,
            headers,
            body: body == null ? body : JSON.stringify(body)
        };
        if (!isBrowser && this.httpsAgent != null) {
            requestConfig["agent"] = this.httpsAgent;
        }
        return requestConfig;
    }

    private static getBaseUrl(): string {
        let baseUrl: string = "";

        if (isBrowser) {
            let context = ContextUtility.getImisClientContext();
            baseUrl = context.baseUrl;
        } else {
            // most likely node
            baseUrl = this.baseUrl;
        }

        if (baseUrl.endsWith("/")) {
            baseUrl = baseUrl.substring(0, baseUrl.length - 1);
        }

        return baseUrl;
    }

}